/**
 * Shared, locale-aware helpers for Meilisearch hits.
 *
 * Used by both result surfaces (search page + header predictive panel) so that
 * localized URLs, money formatting and field normalization stay identical.
 * Exposed as `window.tcSearchResults`; also exported for unit tests.
 */

const ABSOLUTE_URL = /^([a-z][a-z0-9+.-]*:)?\/\//i

const HTML_ESCAPES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

export function escapeHtml(value) {
  if (value == null) return ''
  return String(value).replace(/[&<>"']/g, (character) => HTML_ESCAPES[character])
}

function withoutTrailingSlashes(value) {
  return String(value ?? '').replace(/\/+$/, '')
}

/**
 * Builds a product URL that keeps the current market/language prefix.
 * Index-provided URLs are re-prefixed when they still point at the default
 * locale; absolute URLs are left untouched.
 */
export function resolveHitUrl(hit, rootUrl = '/') {
  const root = withoutTrailingSlashes(rootUrl || '/')
  const raw = typeof hit?.url === 'string' ? hit.url.trim() : ''

  if (raw) {
    if (ABSOLUTE_URL.test(raw)) return raw
    const path = raw.startsWith('/') ? raw : `/${raw}`
    if (!root || path === root || path.startsWith(`${root}/`)) return path
    return `${root}${path}`
  }

  const handle = typeof hit?.handle === 'string' ? hit.handle.replace(/^\/+/, '') : ''
  if (!handle) return '#'
  return `${root}/products/${handle}`
}

function groupDigits(value, separator) {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, separator)
}

function moneyParts(amount) {
  const absolute = Math.abs(amount)
  const totalInCents = Math.round(absolute * 100)
  const whole = Math.floor(totalInCents / 100)
  const decimals = String(totalInCents % 100).padStart(2, '0')
  const rounded = Math.round(absolute)

  return {
    grouped: groupDigits(whole, ','),
    groupedDot: groupDigits(whole, '.'),
    groupedApostrophe: groupDigits(whole, "'"),
    decimals,
    roundedComma: groupDigits(rounded, ','),
    roundedDot: groupDigits(rounded, '.'),
  }
}

// Longest placeholders first so a shorter one can never eat a longer one.
const MONEY_FORMATTERS = {
  '{{amount_no_decimals_with_comma_separator}}': (parts) => parts.roundedDot,
  '{{amount_with_comma_separator}}': (parts) => `${parts.groupedDot},${parts.decimals}`,
  '{{amount_with_apostrophe_separator}}': (parts) => `${parts.groupedApostrophe}.${parts.decimals}`,
  '{{amount_no_decimals}}': (parts) => parts.roundedComma,
  '{{amount}}': (parts) => `${parts.grouped}.${parts.decimals}`,
}

function applyMoneyFormat(amount, template) {
  const parts = moneyParts(amount)
  let output = template
  let replaced = false

  for (const [placeholder, render] of Object.entries(MONEY_FORMATTERS)) {
    if (!output.includes(placeholder)) continue
    output = output.split(placeholder).join(render(parts))
    replaced = true
  }

  if (!replaced || output.includes('{{')) return null
  return output
}

function toNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const text = value.trim().replace(',', '.')
    if (!text) return null
    const parsed = Number(text)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function formatWithIntl(amount, options) {
  const locale = typeof options.locale === 'string' && options.locale ? options.locale : undefined
  const currency =
    typeof options.currencyCode === 'string' && options.currencyCode ? options.currencyCode : undefined

  try {
    if (currency) {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount)
    }
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return amount.toFixed(2)
  }
}

/**
 * Formats an amount expressed in currency units (not cents).
 *
 * Prefers the shop's money format, which is the format the Liquid cards, cart
 * and PDP already print, so Meili results stop disagreeing with native ones.
 * Falls back to the storefront's own locale (never the browser's).
 */
export function formatHitPrice(amount, options = {}) {
  const numeric = toNumber(amount)
  if (numeric === null) return ''

  const template = typeof options.moneyFormat === 'string' ? options.moneyFormat.trim() : ''
  if (template) {
    const formatted = applyMoneyFormat(numeric, template)
    if (formatted) return formatted
  }

  return formatWithIntl(numeric, options)
}

/**
 * Index prices are decimals (`price: "7.48"`, `price_numeric: 7.48`).
 * `price_numeric` is always treated as currency units; a bare integer in
 * `price` is still read as cents for backwards compatibility.
 */
function resolveAmount(value, { assumeUnits = false } = {}) {
  if (value == null || value === '') return null

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null
    if (assumeUnits) return value
    return Number.isInteger(value) ? value / 100 : value
  }

  const text = String(value).trim()
  if (!text) return null

  const isDecimal = text.includes('.') || text.includes(',')
  const parsed = Number(text.replace(',', '.'))
  if (!Number.isFinite(parsed)) return null

  if (isDecimal || assumeUnits) return parsed
  return parsed / 100
}

function firstString(...values) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') return value
  }
  return ''
}

function resolveImage(source) {
  const title = firstString(source.title, source.name, source.product_title, source.variant_title)
  const candidates = [
    source.featured_image,
    source.image,
    Array.isArray(source.images) ? source.images[0] : null,
    source.variant_image,
  ]

  for (const candidate of candidates) {
    if (!candidate) continue
    if (typeof candidate === 'string') return { url: candidate, alt: title }

    const url = candidate.url || candidate.src || ''
    if (url) return { url, alt: firstString(candidate.alt_text, candidate.alt, title) }
  }

  return { url: '', alt: title }
}

export function normalizeHit(hit, options = {}) {
  const source = hit || {}
  const title = firstString(source.title, source.name, source.product_title)

  return {
    id: source.id ?? source.variant_id ?? null,
    title,
    titleHtml: source._formatted?.title || null,
    url: resolveHitUrl(source, options.rootUrl ?? '/'),
    image: resolveImage(source),
    price: resolveAmount(source.price_numeric, { assumeUnits: true }) ?? resolveAmount(source.price),
    compareAtPrice: resolveAmount(source.compare_at_price ?? source.compareAtPrice, {
      assumeUnits: true,
    }),
    available: !(source.available_for_sale === false || source.available === false),
  }
}

if (globalThis.window) {
  globalThis.tcSearchResults = { escapeHtml, formatHitPrice, normalizeHit, resolveHitUrl }
}
